/**
 * Every word the rider app shows, in three languages.
 *
 * - EN: complete English.
 * - HINGLISH: the owner's mockup copy. Only the lines that differ from English are listed; the rest are the same
 *   words in both (the mockup itself mixes Hinglish headings with English detail), so Hinglish falls back to EN.
 * - HI: Hindi — **DRAFT PLACEHOLDERS, NOT REVIEWED**. Only the main headings and buttons are drafted; every missing
 *   key falls back to Hinglish (then English). A native speaker must check and complete HI before it is offered as
 *   finished (docs/OWNER-DECISIONS.md §8, "Hindi").
 *
 * `{name}` is filled in by t(key, { name }); `<key>.one` / `<key>.other` are picked by t.n(key, count).
 */
export const EN = {
  // vehicles (the owner's list is Bike, Auto, Car)
  'vehicle.BIKE': 'Bike',
  'vehicle.AUTO': 'Auto',
  'vehicle.CAR': 'Car',
  'noun.BIKE': 'bike',
  'noun.AUTO': 'auto',
  'noun.CAR': 'car',
  'plural.BIKE': 'bikes',
  'plural.AUTO': 'autos',
  'plural.CAR': 'cars',
  'seats.one': '{n} seat',
  'seats.other': '{n} seats',
  'seats.ac': '{seats} · AC',

  // shared
  'common.ok': 'OK',
  'common.done': 'Done',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.retry': 'Try again',
  'common.loading': 'Loading…',
  'common.free': 'free',
  'common.min': 'min',
  'common.now': 'Now',
  'common.here': 'Here',
  'common.new': 'New',
  'common.yourDriver': 'Your driver',
  'common.yourDriverLower': 'your driver',
  'common.comingSoon': 'Coming soon',
  'common.cancelling': 'Cancelling…',
  'common.rides.one': '{n} ride',
  'common.rides.other': '{n} rides',
  'common.removeX': 'Remove {name}',
  'nav.main': 'Main',
  'nav.ride': 'Ride',
  'nav.trips': 'Trips',
  'nav.account': 'Account',

  // connection and errors (never the server's raw message)
  'net.offline': 'You’re offline',
  'net.offlineBody': 'Showing the last update. We’ll refresh as soon as you’re back.',
  'net.live': 'Live updates paused · refreshing every few seconds',
  'err.network': 'No connection. Check your internet and try again.',
  'err.server': 'Something went wrong on our side. Please try again.',
  'err.busy': 'Too many tries. Wait a moment and try again.',
  'err.generic': 'That didn’t work. Please try again.',
  'err.SIGNIN_NOT_ALLOWED': 'Zoomo Rides is open to a small test group right now.',
  'err.SMS_SEND_FAILED': 'We couldn’t send the code just now. Try again in a moment.',
  'err.SMS_RATE_LIMITED': 'Too many codes to this number. Wait a few minutes and try again.',
  'err.PLACES_UNAVAILABLE': 'Place search is resting. Pick from the list or set the spot on the map.',
  'err.PLACE_NOT_FOUND': 'We don’t have an address for that spot. Drag the pin a little.',
  'err.OUTSIDE_SERVICE_AREA': 'That place is outside our area for now (Jourian, Akhnoor and Jammu).',
  'err.TRIP_TOO_SHORT': 'That’s very close — under 300 m. Pick a place further away.',
  'err.TRIP_TOO_LONG': 'That trip is too long for now (over 80 km).',
  'err.NO_FARES': 'Fares aren’t set up yet. Please try again later.',
  'err.QUOTE_EXPIRED': 'That price ran out. We’ve checked the fare again.',
  'err.QUOTE_INVALID': 'That price is no longer valid. We’ve checked the fare again.',
  'err.QUOTE_USED': 'That price was already used for a booking.',
  'err.VEHICLE_NOT_QUOTED': 'That vehicle isn’t available for this trip.',
  'err.RIDE_ALREADY_OPEN': 'You already have a ride going.',
  'err.REWARD_UNAVAILABLE': 'That reward can’t be used right now.',
  'err.ONLINE_PAYMENTS_UNAVAILABLE': 'Online payment isn’t set up yet. Pay your driver in cash.',
  'err.OTP_WRONG': 'That code isn’t right. Check the SMS and try again.',
  'err.OTP_EXPIRED': 'That code has expired. Ask for a new one.',
  'err.OTP_COOLDOWN': 'A code was sent a moment ago. Wait 30 seconds for a new one.',
  'err.OTP_LIMIT': 'Too many codes for this number. Try again in an hour.',
  'err.OTP_TOO_MANY_ATTEMPTS': 'Too many wrong tries. Ask for a new code.',
  'err.INVALID_PHONE': 'Enter a valid mobile number.',
  'err.INVALID_EMAIL': 'Enter a valid email address.',
  'err.ACCOUNT_BLOCKED': 'This account is blocked. Contact Zoomo Rides.',
  'err.SMS_UNAVAILABLE': 'Sign-in by SMS isn’t available right now. Please try later.',
  'err.ALREADY_TIPPED': 'You already added a tip for this trip.',
  'err.ALREADY_RATED': 'You already rated this trip.',
  'err.PAYMENT_STARTED': 'Payment has started, so the tip can’t be changed now.',
  'err.TIP_INVALID': 'Pick a tip between ₹1 and ₹1,000.',
  'err.ILLEGAL_TRANSITION': 'The ride has moved on — refreshing.',
  'err.RIDE_CHANGED': 'The ride just changed — refreshing.',
  'err.TOO_MANY_CONTACTS': 'You can add up to 5 trusted contacts.',
  'err.CONTACT_EXISTS': 'That number is already a trusted contact.',
  'err.TOO_MANY_LINKS': 'This trip already has 5 live links.',
  'err.RIDE_ENDED': 'This ride has ended.',
  'err.RATE_LIMITED': 'Too many tries. Wait a moment and try again.',
  'err.VALIDATION_FAILED': 'Please check what you typed and try again.',

  // coming soon / not built (gaps — docs/OWNER-DECISIONS.md §8)
  'soon.hindi.title': 'हिंदी — draft',
  'soon.hindi.body': 'Hindi is partly translated. Lines that aren’t translated yet show in Hinglish.',
  'soon.driver.title': 'Drive with Zoomo Rides',
  'soon.driver.body': 'The Zoomo Rides driver app is being tested now. Bike, auto and car owners in Jourian and Akhnoor can sign up as soon as it is out.',
  'soon.terms.title': 'Terms and Privacy Policy',
  'soon.terms.body': 'Our terms and privacy policy will be published here before launch.',
  'soon.whatsapp.title': 'WhatsApp — coming soon',
  'soon.whatsapp.body': 'Sign-in codes and trip updates come by SMS for now.',
  'soon.upi.title': 'Online payment — coming soon',
  'soon.upi.body': 'For now, pay your driver in cash at the drop. The fare you see is the fare you pay.',
  'soon.call.title': 'Calling — coming soon',
  'soon.call.body': 'Calls between riders and drivers will go through Zoomo Rides so neither of you sees the other’s number. Until then, meet at the pickup pin. In an emergency use Safety → SOS or call 112.',
  'soon.message.title': 'Messages — coming soon',
  'soon.message.body': 'Add a note for your driver when you confirm the pickup. In an emergency use Safety → SOS or call 112.',
  'soon.help.title': 'Help',
  'soon.help.body': 'A support line is not set up yet. In an emergency, call 112. For a problem with a ride, add a note when you rate it — our team reads every one.',
  'soon.schedule.title': 'Rides now only',
  'soon.schedule.body': 'Booking for later is not available yet.',

  // welcome
  'welcome.hindi': 'Switch the app to Hindi',
  'welcome.english': 'Switch the app to English',
  'welcome.l1': 'Zoom it.',
  'welcome.l2': 'Ride it.',
  'welcome.l3': 'Reach it.',
  'welcome.lead': 'Bike, auto and car — from Jourian’s own drivers. Fixed fares. Pay cash.',
  'welcome.cta': 'Continue with phone number',
  'welcome.drive': 'Drive with Zoomo Rides?',
  'welcome.signup': 'Sign up',
  'welcome.agree': 'By continuing you agree to our',
  'welcome.terms': 'Terms',
  'welcome.and': 'and',
  'welcome.privacy': 'Privacy Policy',
  'welcome.f1': 'Fare fixed before you book',
  'welcome.f2': 'Pay the driver in cash',
  'welcome.f3': 'Live trip link for family',

  // phone
  'phone.step': 'Step 1 of 2',
  'phone.title': 'Enter your number',
  'phone.lead': 'We’ll text you a {n}-digit code to sign in.',
  'phone.label': 'Mobile number',
  'phone.private': 'Drivers never see your number.',
  'phone.invalid': 'Enter a valid mobile number for {country}.',
  'phone.country': 'Country',
  'phone.countryAria': 'Country code: {name}, {dial}. Change',
  'phone.countrySearch': 'Search a country or code',
  'phone.countryNone': 'No country by that name.',
  'signin.method': 'Sign in with',
  'signin.phone': 'Phone',
  'signin.email': 'Email',
  'email.title': 'Enter your email',
  'email.lead': 'We’ll email you a {n}-digit code to sign in.',
  'email.label': 'Email address',
  'email.invalid': 'Enter a valid email address, like name@example.com.',
  'email.private': 'Only used to sign you in and send your receipts.',
  'acc.addEmail': 'Add email',
  'acc.emailBody': 'For your receipts, and as a second way to sign in. We’ll send it a code.',
  'acc.addPhone': 'Add phone',
  'acc.phoneBody': 'So your driver’s app can reach you, and as a second way to sign in. We’ll text it a code.',
  'acc.codeSent': 'We sent a code to {to}. Enter it here.',
  'acc.verify': 'Verify and save',
  'phone.send': 'Send code',
  'phone.sending': 'Sending…',

  // code
  'code.step': 'Step 2 of 2',
  'code.title': 'Enter the code',
  'code.sent': 'Sent by SMS to {phone}.',
  'code.sentEmail': 'Sent to {email}. Check your inbox.',
  'code.change': 'Change',
  'code.group': '{n}-digit code',
  'code.digit': 'Code digit {i}',
  'code.auto': 'We’ll sign you in as soon as the code is complete.',
  'code.signing': 'Signing you in…',
  'code.resendIn': 'Resend code in 0:{s}',
  'code.resend': 'Resend code',
  'code.wa': 'WhatsApp · soon',
  'code.dev': 'Development code · no SMS yet',
  'code.devFill': 'Fill code {code} (development: no SMS provider yet)',

  // home
  'home.pickupAria': 'Pickup: {place}. Change pickup',
  'home.account': 'Your account',
  'greet.morning': 'Good morning',
  'greet.noon': 'Good afternoon',
  'greet.evening': 'Good evening',
  'greet.night': 'Good night',
  'home.title': 'Where to?',
  'home.search': 'Village, area, landmark…',
  'home.nowAria': 'Ride now (booking for later is not available yet)',
  'home.usually': 'USUALLY NOW',
  'home.setup': 'SET UP',
  'home.home': 'Home',
  'home.work': 'Work',
  'home.saveHome': 'Save your home for one-tap rides',
  'home.saveWork': 'Save your work place',
  'home.bookTo': 'Book an auto to {place}',
  'home.setHome': 'Set your home',
  'home.popular': 'Popular',
  'home.pick': 'Pick a ride',
  'home.looking': 'Looking around…',
  'home.nearby.one': '{n} driver nearby',
  'home.nearby.other': '{n} drivers nearby',
  'home.noneNearby': 'No drivers nearby right now',
  'home.noneNear': 'none near',
  'home.sharing.on': 'After-dark sharing is on',
  'home.sharing.off': 'After-dark sharing is off',
  'home.sharing.add': 'Add trusted contacts to share your trips',
  'home.sharing.onSub': '{names} get your live trip link',
  'home.sharing.offSub': 'Turn on to send {names} your live trip link',
  'home.and': 'and',
  'home.name.title': 'Your name?',
  'home.name.body': 'Your driver sees your first name so they know who to pick up.',
  'home.name.label': 'Your name',
  'home.reward': '{title} — ready for your next ride',

  // search
  'search.trip': 'Your trip',
  'search.pickup': 'Pickup',
  'search.setHome': 'Set {name}',
  'search.setPlace': 'Add a place',
  'search.whereTo': 'Where to?',
  'search.searchPlace': 'Search a place',
  'search.clear': 'Clear',
  'search.onMap': 'Set on map',
  'search.places': 'PLACES',
  'search.nearbyPlaces': 'NEARBY PLACES',
  'search.fixedBy': 'FIXED FARE BY {type}',
  'search.none': 'No place by that name yet. Use “Set on map” to drop the pin where you want to go.',
  'search.about': 'about {n} min',
  'search.current': 'Use current location',
  'search.cantFind': 'Can’t find it? “Set on map” and drag the pin. More places are being added.',
  'search.saved': '{name} saved',
  'search.pinned': 'Pinned spot',
  'search.couldNotSave': 'Could not save. Try again.',

  // choose ride
  'choose.title': 'Choose a ride',
  'choose.route': 'Route from {from} to {to}',
  'choose.fixed': 'Fixed fares · no haggling',
  'choose.getting': 'Getting fixed fares…',
  'choose.away': '{n} min away',
  'choose.min': '{n} min',
  'choose.noneNow': 'none nearby now',
  'choose.fastest': 'fastest',
  'choose.cash': 'Cash',
  'choose.cashSub': 'pay the driver at drop',
  'choose.change': 'Change',
  'choose.book': 'Book {type} · {fare}',
  'choose.bookPlain': 'Book',
  'choose.refreshed': 'Fares refreshed',
  'choose.noDrivers': 'No {plural} free nearby right now. You can still book — we’ll keep asking for {mins} min.',
  'pay.title': 'Payment',
  'pay.cash': 'Cash',
  'pay.cashSub': 'Pay the driver at the drop',
  'pay.online': 'UPI / card (online)',
  'pay.onlineSub': 'Coming soon',
  'pay.reward': 'Use reward: {title}',
  'pay.rewardOff': '{amount} off this ride',
  'pay.noReward': 'No reward',
  'choose.rewardOn': '{amount} off with your reward',

  // confirm pickup
  'pickup.title': 'Confirm pickup',
  'pickup.setDrop': 'Set drop on map',
  'pickup.setHome': 'Set Home on map',
  'pickup.setWork': 'Set Work on map',
  'pickup.setPlace': 'Set the place on map',
  'pickup.confirm': 'Confirm pickup',
  'pickup.dropHere': 'Set drop here',
  'pickup.savePlace': 'Save this place',
  'pickup.pinPickup': 'Pickup here',
  'pickup.pinDrop': 'Drop here',
  'pickup.pinPlace': 'This place',
  'pickup.drag': 'Drag the map to move the pin to the exact spot.',
  'pickup.walk': '{n} min walk from you',
  'pickup.driverComes': 'Your driver comes to this pin',
  'pickup.edit': 'Edit',
  'pickup.note': 'Note for driver, e.g. “red jacket”',
  'pickup.noteAria': 'Note for your driver',
  'pickup.locate': 'Use my location',
  'pickup.locOff': 'Location is off. Drag the map to your spot instead.',
  'pickup.map': 'Map. {pin}: {place}',
  'pickup.placeName': 'Name this place',
  'pickup.placeNameHint': 'e.g. Nani’s house, Gym',
  'pickup.tooMany': 'You can save up to 5 places. Remove one first.',
  'pickup.requoted': 'The price ran out, so we checked again: {fare} — the same. Booking…',
  'pickup.requotedNew': 'The price ran out. The fare is now {fare}. Tap Confirm to book at this fare.',

  // finding
  'find.title': 'Finding your {noun}…',
  'find.lookingMap': 'Looking for {plural} around {area}',
  'find.sheet': 'Finding your {noun}',
  'find.asking': 'Asking the nearest {plural} in {area}',
  'find.bar': '{asked} {plural} asked, {looking} looking at your request',
  'find.asked.one': '{n} {noun} asked',
  'find.asked.other': '{n} {plural} asked',
  'find.noneYet': 'No {plural} free nearby yet',
  'find.lookingN': '{n} looking at it',
  'find.next': 'Asking the next ones',
  'find.still': 'Still trying',
  'find.near': '{type} near {place}',
  'find.in': '{type} in {place}',
  'find.nearby': '{type} nearby',
  'find.away': '{d} away',
  'find.looking': 'Looking',
  'find.cancel': 'Cancel request',
  'find.empty': 'No {plural} online near you right now. We’ll keep trying until {time}.',
  'find.nearbyCount.one': '{n} {noun} online nearby',
  'find.nearbyCount.other': '{n} {plural} online nearby',

  // ended
  'ended.EXPIRED.title': 'No driver this time',
  'ended.EXPIRED.body': 'Nobody near you took the ride in time. Try again, or pick another vehicle.',
  'ended.CANCELLED_BY_DRIVER.title': 'Your driver cancelled',
  'ended.CANCELLED_BY_DRIVER.body': 'Sorry about that. Book again and we will ask the next driver.',
  'ended.CANCELLED_BY_ADMIN.title': 'Zoomo Rides cancelled this ride',
  'ended.CANCELLED_BY_ADMIN.body': 'Our team ended this ride. Book again, or contact us if this was a mistake.',
  'ended.CANCELLED_BY_RIDER.title': 'Ride cancelled',
  'ended.fee': 'A cancellation fee applies.',
  'ended.noCharge': 'No charge.',
  'ended.title': 'Ride ended',
  'ended.again': 'Try again',
  'ended.home': 'Back to home',

  // arriving
  'arr.label': 'Driver arriving',
  'arr.map': '{name}’s {noun} is coming to your pickup',
  'arr.safety': 'Safety',
  'arr.safetyAria': 'Safety tools',
  'arr.sheet': 'Your driver',
  'arr.coming': '{name} is on the way',
  'arr.here': '{name} is here',
  'arr.waiting': 'Waiting at your pickup · {vehicle}',
  'arr.arriving': 'Arriving {time} · {vehicle}',
  'arr.onWay': 'On the way · {vehicle}',
  'arr.pin': 'Your ride PIN',
  'arr.sayPin': 'Say it to {name} to start',
  'arr.pinAria': 'PIN {pin}',
  'arr.you': 'You',
  'arr.youAt': 'You · {place}',
  'arr.call': 'Call',
  'arr.message': 'Message',
  'arr.share': 'Share',
  'arr.cancel': 'Cancel ride',
  'arr.freeUntil': 'free until {time}',
  'arr.fee': '{fee} fee',
  'arr.note': 'Note for driver: “{note}”',

  // on trip
  'trip.label': 'On the trip',
  'trip.map': 'On the way to {place}, {left} left',
  'trip.arrives': 'Arrives {time} · {left} left',
  'trip.sos': 'Emergency SOS',
  'trip.details': 'Trip details',
  'trip.linkFor': 'Live link on for {names}',
  'trip.linkOn': 'Your live trip link is on',
  'trip.shareLive': 'Share your live trip',
  'trip.auto': 'You share every trip after dark',
  'trip.anyone': 'Anyone with the link can follow',
  'trip.send': 'Send a link to someone you trust',
  'trip.stop': 'Stop',
  'trip.share': 'Share',
  'trip.journey': 'Journey',
  'trip.picked': 'Picked up · {place}',
  'trip.passing': 'Passing {place}',
  'trip.onWay': 'On the way',
  'trip.drop': 'Drop · {place}',
  'trip.shareTrip': 'Share trip',
  'trip.callX': 'Call {name}',
  'share.title': 'My Zoomo Rides trip',
  'share.text': 'Follow my {noun} ride live on Zoomo Rides:',
  'share.textWith': 'Follow my {noun} ride with {name} live on Zoomo Rides:',
  'share.copied': 'Trip link copied',
  'share.shared': 'Trip link shared',
  'share.stopped': 'Live link stopped',
  'share.failed': 'Could not make a link. Try again.',

  // arrived
  'done.label': 'Arrived, pay and rate',
  'done.report': 'Report an issue',
  'done.title': 'You’ve arrived!',
  'done.onTime': 'on time',
  'done.payment': 'Payment',
  'done.payCash': 'Pay cash to {name}',
  'done.paidCash': 'Cash paid to {name}',
  'done.waitCash': '{name} confirms the cash on the driver app',
  'done.refunded': 'Refunded',
  'done.onlineFailed': 'Online payment didn’t go through — pay cash to {name}',
  'done.payOnline': 'Online payment',
  'done.upi': 'Pay by UPI',
  'done.fare': 'Fixed fare',
  'done.discount': 'Reward',
  'done.tipFor': 'Tip for {name}',
  'done.addTip': 'Add a tip — all of it goes to {name}',
  'done.tipped': 'You added a {amount} tip',
  'done.handTip': 'Hand the {amount} tip to {name} in cash',
  'done.noTip': 'No tip',
  'done.rate': 'How was your ride with {name}?',
  'done.rated': 'You gave {name} {stars}★',
  'done.ratingAria': 'Rating, {n} of 5 stars',
  'done.stars.one': '{n} star',
  'done.stars.other': '{n} stars',
  'done.btnPay': 'Done · pay {amount} cash',
  'done.btnPaid': 'Done · paid {amount} cash',
  'done.btnTip': 'Done · hand over {amount} tip',
  'done.btn': 'Done',
  'done.issueBody': 'Tell us what went wrong. It goes to the Zoomo Rides team with your rating when you tap Done. In an emergency, call 112.',
  'done.issueAria': 'What went wrong',
  'done.issueHint': 'e.g. asked for more money, rash driving',
  'done.addToRating': 'Add to my rating',
  'done.couldNotSave': 'Could not save. Try again.',
  'tag.SAFE_DRIVING': 'Safe driving',
  'tag.ON_TIME': 'On time',
  'tag.CLEAN_VEHICLE': 'Clean {noun}',
  'tag.POLITE': 'Polite',

  // trips
  'trips.title': 'Your trips',
  'trips.summary': '{rides} · {km} km since {month}',
  'trips.noneDone': 'No completed rides yet',
  'trips.empty': 'Your rides will show here',
  'trips.emptyCta': 'Book your first ride',
  'trips.youGave': 'you gave ★{n}',
  'trips.notRated': 'not rated',
  'trips.again': 'Book again',
  'trips.today': 'TODAY',
  'trips.yesterday': 'YESTERDAY',
  'trips.CANCELLED_BY_RIDER': 'you cancelled',
  'trips.CANCELLED_BY_DRIVER': 'driver cancelled',
  'trips.CANCELLED_BY_ADMIN': 'cancelled by Zoomo Rides',
  'trips.EXPIRED': 'no driver found',
  'trips.cashDue': 'cash due',
  'trips.saved': 'saved {amount}',
  'trips.rewards': '{done} · next: {title} in {n}',
  'trips.rewardsReady': 'Reward ready: {title}',
  'trips.loadError': 'Could not load your trips.',

  // account
  'acc.label': 'Account',
  'acc.your': 'Your account',
  'acc.rider': '{n} rider',
  'acc.newRider': 'new rider',
  'acc.edit': 'Edit profile',
  'acc.safety': 'Safety',
  'acc.contacts': 'Trusted contacts',
  'acc.addFamily': 'Add family or friends',
  'acc.shareDark': 'Share trips after dark',
  'acc.shareDarkSub': 'They get a live link automatically',
  'acc.addFirst': 'Add a trusted contact first',
  'acc.settings': 'Settings',
  'acc.places': 'Saved places',
  'acc.addPlaces': 'Add Home, Work',
  'acc.payment': 'Payment',
  'acc.paymentVal': 'Cash',
  'acc.payReward': 'Cash · {amount} off ready',
  'acc.rewards': 'Rewards',
  'acc.rewardsReady.one': '{n} ready to use',
  'acc.rewardsReady.other': '{n} ready to use',
  'acc.rewardsNext': '{n} to go',
  'acc.rewardsNone': 'None yet',
  'acc.language': 'Language',
  'acc.help': 'Help',
  'acc.support': 'Support',
  'acc.drive': 'Drive with Zoomo Rides',
  'acc.driveSub': 'Earn with your bike, auto or car',
  'acc.logout': 'Log out',
  'acc.version': 'Version {v} · Made in Jourian',
  'acc.nameTitle': 'Your name',
  'acc.nameBody': 'Drivers see your first name.',
  'lang.title': 'Language',
  'lang.en': 'English',
  'lang.hinglish': 'Hinglish',
  'lang.hi': 'हिंदी (draft)',
  'lang.hiNote': 'Hindi is a draft: lines not translated yet show in Hinglish.',
  'rewards.title': 'Rewards',
  'rewards.body': 'You earn rewards automatically as you ride. Use one when you book (Choose a ride → Cash → reward).',
  'rewards.done.one': '{n} ride done',
  'rewards.done.other': '{n} rides done',
  'rewards.toGo.one': '{n} ride to go',
  'rewards.toGo.other': '{n} rides to go',
  'rewards.reached': 'Earned',
  'rewards.ready': 'Ready to use',
  'rewards.applied': 'On a booked ride',
  'rewards.used': 'Used',
  'rewards.placeholder': 'Introductory rewards — amounts may change.',

  // saved places
  'places.title': 'Saved places',
  'places.tap': 'Tap to set',
  'places.add': 'Add a place',
  'places.max': 'Up to 5 places.',
  'places.removed': '{name} removed',
  'places.couldNotRemove': 'Could not remove. Try again.',

  // contacts
  'contacts.title': 'Trusted contacts',
  'contacts.body': 'People you trust to follow your rides. With “Share trips after dark” on, the app makes a live link for every trip after 7 pm — tap Share trip to send it to them.',
  'contacts.none': 'No contacts yet.',
  'contacts.add': 'Add someone',
  'contacts.name': 'Name, e.g. Maa',
  'contacts.nameAria': 'Contact name',
  'contacts.phone': 'Mobile number',
  'contacts.phoneAria': 'Contact mobile number',
  'contacts.addBtn': 'Add contact',
  'contacts.need': 'Enter a name and a valid mobile number',

  // safety
  'sos.title': 'Emergency',
  'sos.sent': 'Help is on the way',
  'sos.body': 'Alert Zoomo Rides support with your trip and location, or call the police and ambulance on 112.',
  'sos.call': 'Call 112',
  'sos.alert': 'Alert Zoomo Rides support',
  'sos.failed': 'Could not reach support. Call 112 now.',
  'sos.ok': 'Zoomo Rides support has been alerted. If you are in danger, call 112.',
  'safety.title': 'Safety',
  'safety.body': 'Check the plate before you get in. Your trip starts only when you tell the driver your PIN.',
  'safety.sos': 'Emergency SOS',
  'safety.share': 'Share live trip',

  // the phone's map sheet
  'sheet.more': 'Show the whole panel',
  'sheet.less': 'Show more of the map',

  // where the phone is, Uber-style pickup
  'loc.current': 'Current location',
  'loc.finding': 'Finding where you are…',

  // choose ride: arrival time and the trip at the top of the panel
  'choose.drop': 'Drop {time}',
  'choose.trip': 'Your trip',
  'choose.editPickup': 'Change pickup: {place}',
  'choose.editDrop': 'Change drop: {place}',
  'choose.seatsAria.one': '{n} seat',
  'choose.seatsAria.other': '{n} seats',

  // search: saved and recent places before typing
  'search.recent': 'RECENT',

  // home: places under the search, and the ride that is on
  'home.recent': 'Recent places',
  'home.live.finding': 'Finding your {noun}…',
  'home.live.coming': '{name} is on the way',
  'home.live.here': '{name} is at your pickup',
  'home.live.trip': 'On the trip to {place}',
  'home.live.done': 'Pay and rate your trip',
  'home.live.open': 'Open',
  'home.live.aria': 'Your ride: {status}. Open it',

  // cancelling with a reason
  'cancel.titleRide': 'Cancel this ride?',
  'cancel.titleRequest': 'Cancel your request?',
  'cancel.why': 'Why are you cancelling?',
  'cancel.r.wait': 'The driver is taking too long',
  'cancel.r.plans': 'My plans changed',
  'cancel.r.mistake': 'I booked by mistake',
  'cancel.r.driver': 'The driver asked me to cancel',
  'cancel.r.pickup': 'Wrong pickup spot',
  'cancel.r.other': 'Something else',
  'cancel.free': 'Free to cancel — no charge.',
  'cancel.freeUntil': 'Free to cancel until {time}. After that, a {fee} fee applies.',
  'cancel.fee': 'The free cancel time has passed: a {fee} fee applies.',
  'cancel.keepRide': 'Keep my ride',
  'cancel.keepRequest': 'Keep looking',
  'cancel.yes': 'Yes, cancel',

  // public trip page
  'track.label': 'Live trip',
  'track.map': 'Live trip map',
  'track.friend': 'Your friend',
  'track.REQUESTED': 'Looking for a driver',
  'track.OFFERING': 'Looking for a driver',
  'track.ACCEPTED': 'Driver on the way to pickup',
  'track.ARRIVED': 'Driver at the pickup',
  'track.STARTED': 'On the trip',
  'track.ended': 'This trip has ended',
  'track.endedBody': 'The live link stops when the ride is over.',
  'track.notFound': 'This link doesn’t work',
  'track.notFoundBody': 'Check that you have the whole link.',
  'track.left': ' · about {mins} min, {left} left (arrives {time})',
  'track.worried': 'Worried? Call 112.',
  'track.driver': 'Driver',
} as const;

export type StringKey = keyof typeof EN;
type Dict = Partial<Record<StringKey, string>>;

/** The mockup's Hinglish lines (everything else reads the same as English). */
export const HINGLISH: Dict = {
  'welcome.l3': 'Pahunch gaye.',
  'phone.title': 'Apna number daalein',
  'email.title': 'Apna email daalein',
  'code.title': 'Code daalein',
  'greet.morning': 'Subah ho gayi',
  'greet.noon': 'Dopahar ho gayi',
  'greet.evening': 'Shaam ho gayi',
  'greet.night': 'Raat ho gayi',
  'home.title': 'Kahan chalein?',
  'home.search': 'Gaon, mohalla, landmark…',
  'home.home': 'Ghar',
  'home.work': 'Kaam',
  'home.saveHome': 'Ghar save karein — one-tap rides',
  'home.saveWork': 'Kaam ki jagah save karein',
  'home.name.title': 'Aapka naam?',
  'acc.addPlaces': 'Add Ghar, Kaam',
  'pickup.setHome': 'Set Ghar on map',
  'pickup.setWork': 'Set Kaam on map',
  'done.title': 'Pahunch gaye!',
  'done.rate': '{name} ke saath ride kaisi thi?',
  'find.title': '{type} dhoond rahe hain…',
  'arr.coming': '{name} aa rahe hain',
  'arr.here': '{name} pahunch gaye',
  'net.offline': 'Net nahi hai',
  'ended.EXPIRED.title': 'Is baar driver nahi mila',
  'home.live.finding': '{type} dhoond rahe hain…',
  'home.live.coming': '{name} aa rahe hain',
  'home.live.here': '{name} pahunch gaye',
  'cancel.titleRide': 'Ride cancel karein?',
  'cancel.why': 'Cancel kyun kar rahe hain?',
  'cancel.keepRide': 'Ride rakhein',
  'cancel.keepRequest': 'Dhoondte rahein',
  'cancel.yes': 'Haan, cancel karein',
};
/**
 * Hindi — DRAFT PLACEHOLDERS (machine-drafted, not reviewed by a native speaker). Missing keys fall back to
 * Hinglish. Do not ship Hindi as "done" until this is reviewed and completed.
 */
export const HI: Dict = {
  'vehicle.BIKE': 'बाइक',
  'vehicle.AUTO': 'ऑटो',
  'vehicle.CAR': 'कार',
  'nav.ride': 'राइड',
  'nav.trips': 'यात्राएँ',
  'nav.account': 'खाता',
  'common.ok': 'ठीक है',
  'common.done': 'हो गया',
  'common.save': 'सेव करें',
  'common.cancel': 'रद्द करें',
  'common.retry': 'फिर कोशिश करें',
  'welcome.l1': 'ज़ूम करें।',
  'welcome.l2': 'राइड करें।',
  'welcome.l3': 'पहुँच गए।',
  'welcome.cta': 'फ़ोन नंबर से आगे बढ़ें',
  'phone.title': 'अपना नंबर डालें',
  'phone.send': 'कोड भेजें',
  'code.title': 'कोड डालें',
  'home.title': 'कहाँ चलें?',
  'home.search': 'गाँव, मोहल्ला, लैंडमार्क…',
  'home.home': 'घर',
  'home.work': 'काम',
  'home.pick': 'राइड चुनें',
  'choose.title': 'राइड चुनें',
  'pickup.title': 'पिकअप पक्का करें',
  'pickup.confirm': 'पिकअप पक्का करें',
  'find.title': '{type} ढूँढ रहे हैं…',
  'find.cancel': 'अनुरोध रद्द करें',
  'arr.coming': '{name} आ रहे हैं',
  'arr.here': '{name} पहुँच गए',
  'arr.pin': 'आपका राइड PIN',
  'done.title': 'पहुँच गए!',
  'done.payCash': '{name} को नकद दें',
  'trips.title': 'आपकी यात्राएँ',
  'trips.again': 'फिर से बुक करें',
  'acc.language': 'भाषा',
  'acc.logout': 'लॉग आउट',
  'sos.call': '112 पर कॉल करें',
};
